import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { forEach, isEmpty } from 'lodash';
import { getFollowUpQuestions, pushGaStepEvent, putFollowUpQuestions } from 'actions/loan';
import { track } from 'actions/analytics';
import { fieldNames, questionsId } from 'components/Credit24/LoanApplication/FollowUp/const';

import FollowUp from '../../../components/Credit24/LoanApplication/FollowUp';
import sessionRepository from '../../../utils/sessionRepository';

const FollowUpContainer = (props) => {
  const hasTrackedPageData = useRef(false);
  const goToThankYouPage = () => props.router.push(`/success${location.search}${location.hash}`);

  // check if user need to fill anything
  useEffect(() => {
    const { followUpQuestions, followUpQuestionsLoaded, followUpQuestionsFetchFail } = props.loan;
    const isFetchError = sessionRepository.getItem('followUpError');

    // eslint-disable-next-line max-len
    if ((followUpQuestions.length === 0 && followUpQuestionsLoaded) || isFetchError || followUpQuestionsFetchFail) {
      goToThankYouPage();
    } else if (followUpQuestionsLoaded && followUpQuestions.length > 0) {
      if (INCREASE_APP && !hasTrackedPageData.current) {
        props.track('page_data', {}, 'new');
        hasTrackedPageData.current = true;
      }
      props.pushGaStepEvent('follow-up_questions');
    }
  }, [props.loan.followUpQuestions, props.loan.followUpQuestionsLoaded]);

  // check if user already fill the followup
  useEffect(() => {
    const isFilledByUser = props.loan.areAdditionalQuestionsAnswered || sessionRepository.getItem('areAdditionalQuestionsAnswered');
    const isFetchError = sessionRepository.getItem('followUpError');
    const { followUpQuestionsLoaded, skipProvisioAU } = props.loan;

    if (isFilledByUser || isFetchError || skipProvisioAU) {
      goToThankYouPage();
    } else if (!followUpQuestionsLoaded) {
      props.getFollowUpQuestions();
    }
  }, []);

  const getCheckboxText = (fieldName, fieldValue) => document.querySelector(`input[name='${fieldName}'][value='${fieldValue}']`).nextElementSibling.children[0].innerText;

  const onSubmit = (data) => {
    const submittedFields = Object.keys(data);
    const ongoingTransfersData = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const field in data) {
      if (field.includes('ongoingTransfer')) {
        ongoingTransfersData[field] = data[field];
      }
    }

    const lendersData = props.loan.followUpQuestions
      .filter((question) => question.questionId === questionsId.unsureLenderRepayment);
    const recentEnquiryData = props.loan.followUpQuestions
      .filter((question) => question.questionId === questionsId.recentCreditEnquiry);

    let answers = {};

    forEach(submittedFields, (field) => {
      // ALTERNATIVE_INCOME question
      if (field === fieldNames.alternativeIncomeAmount) {
        const amount = Number(data[fieldNames.alternativeIncomeAmount].replaceAll(',', ''));

        const ALTERNATIVE_INCOME = {
          frequency: data[fieldNames.alternativeIncomeFrequency],
          answer: amount,
          '@type': 'AlternativeIncomeAnswer',
        };

        answers = { ...answers, ALTERNATIVE_INCOME };
      }

      //  ESSENTIAL_NEEDS_AS_LOAN_PURPOSE question
      if (field === fieldNames.essentialNeeds) {
        let ESSENTIAL_NEEDS_AS_LOAN_PURPOSE = {
          essentialNeedsPurpose: data[fieldNames.essentialNeeds],
          // eslint-disable-next-line max-len
          fullSelectedCheckboxText: getCheckboxText(fieldNames.essentialNeeds, data[fieldNames.essentialNeeds]),
          '@type': 'EssentialNeedsAsLoanPurposeAnswer',
        };

        if (data[fieldNames.essentialNeeds] === 'OTHER') {
          ESSENTIAL_NEEDS_AS_LOAN_PURPOSE = {
            ...ESSENTIAL_NEEDS_AS_LOAN_PURPOSE,
            otherClarification: data[fieldNames.essentialNeedsClarification],
          };
        }

        answers = { ...answers, ESSENTIAL_NEEDS_AS_LOAN_PURPOSE };
      }

      // HOUSING_EXPENSES_SHARE question
      if (field === fieldNames.housingExpenses) {
        let HOUSING_EXPENSES_SHARE = {
          housingType: data[fieldNames.housingExpenses],
          // eslint-disable-next-line max-len
          fullSelectedCheckboxText: getCheckboxText(fieldNames.housingExpenses, data[fieldNames.housingExpenses]),
          '@type': 'HousingExpensesShareAnswer',
        };

        if (data[fieldNames.housingExpenses] === 'OTHER') {
          HOUSING_EXPENSES_SHARE = {
            ...HOUSING_EXPENSES_SHARE,
            otherClarification: data[fieldNames.housingExpensesClarification],
          };
        }

        answers = { ...answers, HOUSING_EXPENSES_SHARE };
      }

      //  OTHER_EXPENSES question
      if (field === fieldNames.otherExpenses) {
        const OTHER_EXPENSES = {
          checkboxValue: data[fieldNames.otherExpenses],
          // eslint-disable-next-line max-len
          fullSelectedCheckboxText: getCheckboxText(fieldNames.otherExpenses, data[fieldNames.otherExpenses]),
          otherExpensesTextField: data[fieldNames.otherExpensesClarification],
          '@type': 'OtherExpensesAnswer',
        };

        answers = { ...answers, OTHER_EXPENSES };
      }

      // UNDECLARED_CENTRELINK question
      if (field === fieldNames.governmentBenefits) {
        let UNDECLARED_CENTRELINK = {
          answer: data[fieldNames.governmentBenefits],
          // eslint-disable-next-line max-len
          fullSelectedCheckboxText: getCheckboxText(fieldNames.governmentBenefits, data[fieldNames.governmentBenefits]),
          '@type': 'UndeclaredCentrelinkAnswer',
        };

        if (data[fieldNames.governmentBenefits] === 'NO') {
          UNDECLARED_CENTRELINK = {
            ...UNDECLARED_CENTRELINK,
            otherClarification: data[fieldNames.governmentBenefitsClarification],
          };
        }

        answers = { ...answers, UNDECLARED_CENTRELINK };
      }
    });

    // UNSURE_LENDER_REPAYMENT question
    if (lendersData.length > 0) {
      const { otherLendersFunds } = lendersData[0];

      const UNSURE_LENDER_REPAYMENT = {
        '@type': 'UnsureLenderRepaymentAnswer',
        lenders: [],
      };

      forEach(otherLendersFunds, (lender) => {
        const fieldValue = data[`unsureLenderAmount-${lender.lenderName.replaceAll(/[^A-Z0-9]+/ig, '')}`];
        const declaredRepaymentValue = Number(fieldValue.replaceAll(',', ''));

        UNSURE_LENDER_REPAYMENT.lenders.push({
          name: lender.lenderName,
          creditAmount: Number(lender.creditedAmount),
          declaredRepaymentValue,
          frequency: data[`unsureLenderFrequency-${lender.lenderName.replaceAll(/[^A-Z0-9]+/ig, '')}`],
        });
      });

      answers = { ...answers, UNSURE_LENDER_REPAYMENT };
    }

    // ONGOING_TRANSFERS question
    if (!isEmpty(ongoingTransfersData)) {
      const ONGOING_TRANSFERS = {
        '@type': 'OngoingTransfersAnswer',
        answers: [],
      };

      const ongoingTransfersArray = Object.keys(ongoingTransfersData);

      forEach(ongoingTransfersArray, (field) => {
        if (field.includes(`${fieldNames.ongoingTransfers}-`)) {
          const questionId = field.split('-')[1];

          let ONGOING_TRANSFERS_ANSWER = {
            // eslint-disable-next-line max-len
            checkboxText: getCheckboxText(`${fieldNames.ongoingTransfers}-${questionId}`, data[`${fieldNames.ongoingTransfers}-${questionId}`]),
            checkboxValue: data[`${fieldNames.ongoingTransfers}-${questionId}`],
          };

          if (data[`${fieldNames.ongoingTransfers}-${questionId}`] === 'YES') {
            const monthlyValue = Number(data[`${fieldNames.ongoingTransferExtraPartAmount}-${questionId}`].replaceAll(',', ''));

            ONGOING_TRANSFERS_ANSWER = {
              ...ONGOING_TRANSFERS_ANSWER,
              monthlyValue,
              type: data[`${fieldNames.ongoingTransfersExtraPart}-${questionId}`],
            };
          }

          if (data[`${fieldNames.ongoingTransfersExtraPart}-${questionId}`] === 'OTHER') {
            ONGOING_TRANSFERS_ANSWER = {
              ...ONGOING_TRANSFERS_ANSWER,
              otherClarification: data[`${fieldNames.ongoingTransfersExtraPartClarification}-${questionId}`],
            };
          }

          ONGOING_TRANSFERS.answers = [...ONGOING_TRANSFERS.answers, ONGOING_TRANSFERS_ANSWER];
        }
      });

      answers = { ...answers, ONGOING_TRANSFERS };
    }

    // RECENT_CREDIT_ENQUIRY question
    if (recentEnquiryData.length > 0) {
      const { otherLendersApplications } = recentEnquiryData[0];

      const RECENT_CREDIT_ENQUIRY = {
        '@type': 'RecentCreditEnquiryAnswer',
        lenders: [],
      };

      forEach(otherLendersApplications, (lender) => {
        const lenderId = lender.lenderName.replaceAll(/[^A-Z0-9]+/ig, '');
        const answer = data[`${fieldNames.creditEnquiry}-${lenderId}`];
        const answerText = getCheckboxText(`${fieldNames.creditEnquiry}-${lenderId}`, answer);

        let singleLender = {
          name: lender.lenderName,
          creditAmount: Number(lender.creditedAmount),
          answer,
          answerText,
        };

        if (answer !== 'NO') {
          const amount = data[`${fieldNames.creditEnquiryAmount}-${lenderId}`];
          const amountValue = amount && Number(amount.replaceAll(',', ''));
          const frequency = data[`${fieldNames.creditEnquiryFrequency}-${lenderId}`];

          singleLender = {
            ...singleLender,
            amountValue,
            frequency,
          };
        }

        RECENT_CREDIT_ENQUIRY.lenders.push(singleLender);
      });

      answers = { ...answers, RECENT_CREDIT_ENQUIRY };
    }

    return props.putFollowUpQuestions(answers).then(() => {
      sessionRepository.saveItem('areAdditionalQuestionsAnswered', true);
      goToThankYouPage();
    });
  };

  return (
    <div className="container">
      <FollowUp
        followUpQuestions={props.loan.followUpQuestions}
        followUpQuestionsLoaded={props.loan.followUpQuestionsLoaded}
        onSubmit={onSubmit}
      />
    </div>
  );
};

FollowUpContainer.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  getFollowUpQuestions: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  putFollowUpQuestions: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  pushGaStepEvent: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  track: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  loan: PropTypes.object,
  // eslint-disable-next-line react/no-unused-prop-types
  router: PropTypes.object,
};

export default withRouter(connect((state) => ({
  loan: state.loan,
}), {
  getFollowUpQuestions,
  putFollowUpQuestions,
  pushGaStepEvent,
  track,
})(FollowUpContainer));
